import { useState } from "react";
import {
  AuthShell,
  AuthAlert,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from "@/components/auth/auth-shell";
import { LocaleLink } from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import { useForgotPassword } from "@/hooks/use-tp-backend-auth";
import { useTranslation } from "@/common/providers/i18n-provider";

/**
 * "Forgot password" request form, ported from tianpan-v3-web's
 * `src/pages/auth/forgot-password.tsx`. Fires the `forgotPassword` mutation
 * (always resolves `true` server-side regardless of whether the email
 * matches an account, to avoid leaking account existence) and swaps to a
 * "check your inbox" confirmation state — there is no error state to show
 * the user beyond a network/transport failure.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { forgotPassword, loading, error, success } = useForgotPassword();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      await forgotPassword({ variables: { email } });
      setSubmitted(true);
    } catch {
      setErrorMessage(t("auth.forgot.failed"));
    }
  };

  const isSent = submitted || success;

  return (
    <AuthShell
      eyebrow={t("auth.recover.eyebrow")}
      title={isSent ? t("auth.forgot.sentTitle") : t("auth.forgot.title")}
      subtitle={
        isSent
          ? t("auth.forgot.sentSubtitle")
          : t("auth.forgot.subtitle")
      }
      footer={
        <>
          <LocaleLink to="/auth/login" className="text-primary hover:underline">
            {t("auth.link.returnToSignIn")}
          </LocaleLink>
          {!isSent && (
            <LocaleLink to="/auth/sign-up" className="text-primary hover:underline">
              {t("auth.forgot.toSignUp")}
            </LocaleLink>
          )}
        </>
      }
    >
      {!isSent && (
        <>
          <AuthAlert message={errorMessage || error?.message} />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className={AUTH_LABEL_CLASS}>
                {t("auth.field.email")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className={AUTH_INPUT_CLASS}
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full" size="lg">
              {loading ? t("auth.sending") : t("auth.forgot.submit")}
            </Button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
